/**
 * Configurador do Kit MC2100A — Relaxmedic
 *
 * Adiciona ao carrinho o compressor + acessorios escolhidos como linhas
 * separadas (SKUs reais), marcadas com `_kit_id` e `_kit_role`. A Cart
 * Transform Function agrupa essas linhas numa linha-pai com o desconto.
 *
 * Sem dependencias. Funciona em qualquer tema OS 2.0.
 */
(function () {
  "use strict";

  const PORTES = ["Padrão", "Plus"];

  const minuscula = (s) => String(s).toLowerCase();

  class KitConfigurator extends HTMLElement {
    connectedCallback() {
      try {
        this.catalogo = JSON.parse(
          this.querySelector("[data-kit-catalogo]").textContent
        );
      } catch (e) {
        console.error("[kit] catálogo inválido", e);
        return;
      }

      this.aplicarDemo();

      this.tabela = JSON.parse(this.dataset.desconto || "{}");
      this.porteGlobal = 0;
      this.selecao = new Map(); // chave -> indice do tamanho

      this.lista = this.querySelector("[data-kit-lista]");
      this.resumo = this.querySelector("[data-kit-resumo]");
      this.erro = this.querySelector("[data-kit-erro]");
      this.botao = this.querySelector("[data-kit-add]");

      this.renderLista();
      this.ligarEventos();
      this.atualizarRotulosDePorte();
      this.render();
    }

    /**
     * Com ?demo=1 na URL, completa a lista com os acessorios ainda em rascunho
     * (dados reais de SKU e preco). Visualizacao apenas: sem o parametro, nada
     * acontece. Remover junto com o bloco data-kit-demo do snippet quando os 4
     * acessorios estiverem publicados.
     */
    aplicarDemo() {
      let ativo = false;
      try {
        ativo = new URLSearchParams(location.search).get("demo") === "1";
      } catch {
        return;
      }
      if (!ativo) return;

      const fonte = this.querySelector("[data-kit-demo]");
      if (!fonte) return;

      let demo;
      try {
        demo = JSON.parse(fonte.textContent);
      } catch (e) {
        console.error("[kit] dados de demo invalidos", e);
        return;
      }

      const existentes = new Set(this.catalogo.acessorios.map((a) => a.chave));
      const faltantes = (demo.acessorios || []).filter(
        (a) => !existentes.has(a.chave)
      );
      if (faltantes.length === 0) return;

      this.catalogo.acessorios = [...this.catalogo.acessorios, ...faltantes];

      const ordem = demo.ordem || [];
      if (ordem.length) {
        this.catalogo.acessorios.sort(
          (a, b) => ordem.indexOf(a.chave) - ordem.indexOf(b.chave)
        );
      }

      const faixa = this.querySelector("[data-kit-demo-faixa]");
      if (faixa) faixa.hidden = false;
    }

    /* ---------------------------------------------------------------- setup */

    ligarEventos() {
      this.querySelectorAll('input[name="kit-porte"]').forEach((el) => {
        el.addEventListener("change", () => {
          this.porteGlobal = Number(el.value);
          // O porte geral e um atalho: reaplica a todos os selecionados.
          for (const chave of this.selecao.keys()) {
            this.selecao.set(chave, this.porteGlobal);
          }
          this.render();
        });
      });

      this.querySelector("[data-kit-selecionar-todos]").addEventListener(
        "click",
        () => {
          const todos = this.selecao.size === this.catalogo.acessorios.length;
          this.selecao.clear();
          if (!todos) {
            for (const a of this.catalogo.acessorios) {
              if (this.tamanhoDisponivel(a, this.porteGlobal)) {
                this.selecao.set(a.chave, this.porteGlobal);
              }
            }
          }
          this.sincronizarInputs();
          this.render();
        }
      );

      this.botao.addEventListener("click", () => this.adicionar());
    }

    renderLista() {
      this.lista.innerHTML =
        this.markupCentral(this.catalogo.compressor) +
        this.catalogo.acessorios.map((a) => this.markupAcessorio(a)).join("");

      this.lista.querySelectorAll("[data-kit-check]").forEach((el) => {
        el.addEventListener("change", () => {
          const chave = el.dataset.kitCheck;
          if (el.checked) this.selecao.set(chave, this.porteGlobal);
          else this.selecao.delete(chave);
          this.render();
        });
      });

      this.lista.querySelectorAll("[data-kit-pill]").forEach((el) => {
        el.addEventListener("click", () => {
          const chave = el.dataset.kitPill;
          if (!this.selecao.has(chave)) return;
          this.selecao.set(chave, Number(el.dataset.idx));
          this.render();
        });
      });
    }

    markupCentral(c) {
      const img = c.imagem
        ? `<img class="kit-card__img" src="${this.esc(c.imagem)}" alt="" loading="lazy" width="64" height="64">`
        : `<span class="kit-card__img kit-card__img--vazia" aria-hidden="true"></span>`;
      return `
        <div class="kit-card kit-card--central is-selecionado" data-kit-central>
          <div class="kit-card__main" aria-label="${this.esc(c.titulo)} — inclusa no sistema">
            <input type="checkbox" class="kit-card__input" checked disabled aria-hidden="true" tabindex="-1">
            <span class="kit-card__check kit-card__check--travado" aria-hidden="true"></span>
            ${img}
            <span class="kit-card__info">
              <span class="kit-card__titulo">${this.esc(c.titulo)}</span>
              <span class="kit-card__legenda">${this.esc(c.legenda || "")}</span>
              <span class="kit-card__meta">${this.moeda(c.preco)}</span>
            </span>
            <span class="kit-card__selo">Inclusa</span>
          </div>
        </div>`;
    }

    markupAcessorio(a) {
      const pilulas = a.tamanhos
        .map(
          (t, i) =>
            `<button type="button" class="kit-pill" role="radio" aria-checked="false"
                     data-kit-pill="${this.esc(a.chave)}" data-idx="${i}"
                     ${t.disponivel ? "" : "disabled"}
                     title="${t.disponivel ? "" : "Indisponível"}">${this.esc(t.rotulo)}</button>`
        )
        .join("");

      const img = a.imagem
        ? `<img class="kit-card__img" src="${this.esc(a.imagem)}" alt="" loading="lazy" width="64" height="64">`
        : `<span class="kit-card__img kit-card__img--vazia" aria-hidden="true"></span>`;

      return `
        <div class="kit-card" data-kit-item="${this.esc(a.chave)}">
          <label class="kit-card__main">
            <input type="checkbox" class="kit-card__input" data-kit-check="${this.esc(a.chave)}">
            <span class="kit-card__check" aria-hidden="true"></span>
            ${img}
            <span class="kit-card__info">
              <span class="kit-card__titulo">${this.esc(a.titulo)}</span>
              ${a.legenda ? `<span class="kit-card__legenda">${this.esc(a.legenda)}</span>` : ""}
              <span class="kit-card__meta" data-kit-preco="${this.esc(a.chave)}"></span>
            </span>
          </label>
          <div class="kit-card__tamanho" hidden>
            <span class="kit-card__tamanho-rotulo">Tamanho</span>
            <div class="kit-card__pills" role="radiogroup"
                 aria-label="Tamanho — ${this.esc(a.titulo)}">
              ${pilulas}
            </div>
          </div>
        </div>`;
    }

    render() {
      for (const a of this.catalogo.acessorios) {
        const item = this.lista.querySelector(`[data-kit-item="${a.chave}"]`);
        const marcado = this.selecao.has(a.chave);
        const idx = marcado ? this.selecao.get(a.chave) : this.porteGlobal;
        const tamanho = a.tamanhos[idx];

        item.classList.toggle("is-selecionado", marcado);

        const wrapper = item.querySelector(".kit-card__tamanho");
        wrapper.hidden = !marcado;
        wrapper.querySelectorAll("[data-kit-pill]").forEach((b) => {
          const ativo = Number(b.dataset.idx) === idx;
          b.classList.toggle("is-ativo", ativo);
          b.setAttribute("aria-checked", String(ativo));
        });

        const preco = item.querySelector("[data-kit-preco]");
        preco.textContent = tamanho
          ? `Tamanho ${tamanho.rotulo} · ${this.moeda(tamanho.preco)}`
          : "indisponível";
      }

      this.renderResumo();
    }

    renderResumo() {
      const itens = this.itensSelecionados();
      const bruto = itens.reduce((s, i) => s + i.preco, 0);
      const qtd = itens.length - 1;
      const pct = this.tabela[qtd] || 0;
      const liquido = Math.round(bruto * (1 - pct / 100));
      const economia = bruto - liquido;

    this.querySelectorAll("[data-kit-tl-n]").forEach((el) => {
      el.classList.toggle("kit__tl-item--ativo", Number(el.dataset.kitTlN) <= qtd);
    });

      const linhas = itens
        .map(
          (i) =>
            `<li><span>${this.esc(i.titulo)}</span><span>${this.moeda(i.preco)}</span></li>`
        )
        .join("");

      this.resumo.innerHTML = `
        <ul class="kit__linhas">${linhas}</ul>
        ${
          economia > 0
            ? `<p class="kit__economia">
                 <span>Desconto do kit (−${pct}%)</span>
                 <span>−${this.moeda(economia)}</span>
               </p>`
            : ""
        }
        <p class="kit__total">
          <span class="kit__total-rotulo">Total</span>
          <span class="kit__total-valor">
            ${economia > 0 ? `<s class="kit__de">${this.moeda(bruto)}</s>` : ""}${this.moeda(liquido)}
          </span>
        </p>`;

      this.botao.disabled = false;
      this.botao.textContent =
        qtd === this.catalogo.acessorios.length && qtd > 0
          ? "Adicionar kit completo"
          : "Adicionar ao carrinho";
    }

    atualizarRotulosDePorte() {
      // Os acessorios nao usam a mesma grade (botas GG/XG, braco e lombar M/G).
      // Em vez de nomear o porte com um tamanho so ("Padrao (GG)"), mostra o
      // tamanho real de cada peca: "Botas GG · Braço e lombar M".
      this.querySelectorAll("[data-kit-porte-rotulo]").forEach((el) => {
        const i = Number(el.dataset.kitPorteRotulo);
        const grupos = new Map(); // rotulo -> [nomes curtos]
        for (const a of this.catalogo.acessorios) {
          const t = a.tamanhos[i];
          if (!t) continue;
          const nome = a.curto || a.titulo;
          if (!grupos.has(t.rotulo)) grupos.set(t.rotulo, []);
          const lista = grupos.get(t.rotulo);
          if (!lista.includes(nome)) lista.push(nome);
        }
        const partes = [...grupos.entries()].map(([rotulo, nomes]) => {
          const txt = nomes.length > 1
            ? `${nomes.slice(0, -1).join(", ")} e ${minuscula(nomes[nomes.length - 1])}`
            : nomes[0];
          return `${txt} ${rotulo}`;
        });
        el.textContent = partes.join(" · ");
        el.closest(".kit__porte-opcao")?.setAttribute(
          "aria-label",
          `${PORTES[i]}: ${partes.join(", ")}`
        );
      });
    }

    sincronizarInputs() {
      this.lista.querySelectorAll("[data-kit-check]").forEach((el) => {
        el.checked = this.selecao.has(el.dataset.kitCheck);
      });
    }

    /* ---------------------------------------------------------------- dados */

    itensSelecionados() {
      const c = this.catalogo.compressor;
      const itens = [
        {
          titulo: c.titulo,
          variantId: c.variantId,
          preco: c.preco,
          role: "compressor",
        },
      ];

      for (const a of this.catalogo.acessorios) {
        if (!this.selecao.has(a.chave)) continue;
        const t = a.tamanhos[this.selecao.get(a.chave)];
        if (!t || !t.disponivel) continue;
        itens.push({
          titulo: `${a.titulo} (${t.rotulo})`,
          variantId: t.variantId,
          preco: t.preco,
          role: "acessorio",
        });
      }
      return itens;
    }

    tamanhoDisponivel(acessorio, idx) {
      const t = acessorio.tamanhos[idx];
      return Boolean(t && t.disponivel);
    }

    /* ------------------------------------------------------------- carrinho */

    async adicionar() {
      const itens = this.itensSelecionados();
      const kitId = this.novoKitId();

      this.erro.hidden = true;
      this.botao.disabled = true;
      this.botao.setAttribute("aria-busy", "true");

      const payload = {
        items: itens.map((i) => ({
          id: i.variantId,
          quantity: 1,
          properties: { _kit: "cryothermax", _kit_id: kitId, _kit_role: i.role },
        })),
      };

      try {
        const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const corpo = await res.json().catch(() => ({}));
          throw new Error(corpo.description || corpo.message || `HTTP ${res.status}`);
        }

        const detail = { kitId, itens };
        document.dispatchEvent(new CustomEvent("kit:adicionado", { detail }));

        // Avisa o tema (Focal/Dawn escutam no documentElement) para atualizar
        // contador e drawer. Nao dependemos disso: em seguida vamos ao carrinho,
        // que e o unico caminho garantido de o cliente ver o kit adicionado.
        for (const nome of ["cart:refresh", "cart:updated", "cart:change"]) {
          document.documentElement.dispatchEvent(
            new CustomEvent(nome, { bubbles: true, detail: { ...detail, open: true } })
          );
        }

        this.botao.textContent = "Adicionado ✓ Indo para o carrinho…";
        window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
        return;
      } catch (e) {
        console.error("[kit] falha ao adicionar", e);
        this.erro.textContent =
          "Não foi possível adicionar ao carrinho: " + (e.message || "tente novamente");
        this.erro.hidden = false;
      } finally {
        this.botao.disabled = false;
        this.botao.removeAttribute("aria-busy");
      }
    }

    /* --------------------------------------------------------------- utils */

    novoKitId() {
      if (crypto?.randomUUID) return crypto.randomUUID();
      return `kit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    moeda(centavos) {
      const valor = Number(centavos || 0) / 100;
      try {
        return valor.toLocaleString("pt-BR", {
          style: "currency",
          currency: this.dataset.moeda || "BRL",
        });
      } catch {
        return `R$ ${valor.toFixed(2)}`;
      }
    }

    esc(s) {
      return String(s ?? "").replace(/[&<>"']/g, (c) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
      })[c]);
    }
  }

  if (!customElements.get("kit-configurator")) {
    customElements.define("kit-configurator", KitConfigurator);
  }
})();
